import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, H5, P, Divider } from "orcs-design-system";
import { isEmpty } from "lodash";
import { useHistory, useLocation } from "react-router-dom";
import { useGoalsExploreContext } from "src/contexts/goalsExploreContext";
import { useGetObjectives } from "src/contexts/goalsExploreContext/hooks";
import TreeViewNext from "src/shared/TreeViewNext";
import type { CustomizeTreeHeaderFn } from "src/shared/TreeViewNext";
import useFetchRootTeams from "src/hooks/useFetchRootTeams";
import { ROOT_TYPES_FILTER_KEY } from "src/consts/objectives";
import URL, {
  url,
  goalIdFromUrl,
  isOnGoalsExploreRoot,
} from "src/consts/urlPaths";
import { MAX_CHILD_DEPTH } from "src/pages/GoalsExplorer/consts";
import ObjectiveTreeHeader from "./components/ObjectiveTreeHeader";
import {
  findGoalNodeIdInTree,
  findGoalPathToNode,
  isNonSelectableGoalsTreeNode,
  matchesGoalExploreTarget,
} from "./utils/objectivesTree.util";
import useDisplayTreeData, {
  type DisplayTreeObjectiveNode,
} from "./hooks/useDisplayTreeData";

type ObjectiveNode = DisplayTreeObjectiveNode;

const GoalsExplore = () => {
  const history = useHistory();
  const location = useLocation();
  const activeGoalId = goalIdFromUrl(location.pathname);
  const onGoalsExploreRoot = isOnGoalsExploreRoot(location.pathname);
  const {
    treeData,
    loading,
    error,
    loadBranch,
    handleRootDataLoaded,
    handleAppendRootData,
    initialDataFetched,
    rootTeam,
  } = useGoalsExploreContext();

  const [expandedNodeIds, setExpandedNodeIds] = useState<
    Record<string, boolean>
  >({});
  const userCollapsedIdsRef = useRef(new Set<string>());
  const branchLoadedForRef = useRef<string | null>(null);

  useEffect(() => {
    userCollapsedIdsRef.current.clear();
    branchLoadedForRef.current = null;
  }, [activeGoalId, onGoalsExploreRoot]);

  // Sidenav always loads root hierarchy when context is empty — independent of the
  // current route (Explore drawer opens from /teams, /people, etc.). Page branch
  // loads wait for initialDataFetched so LOAD_ROOT_OBJECTIVES is not raced/overwritten.
  const needsRootExplorerData = !initialDataFetched || !rootTeam;

  const { rootTeams } = useFetchRootTeams({
    skip: !needsRootExplorerData,
  } as never);

  const {
    loading: rootLoading,
    error: rootError,
    hasMore,
    totalCount,
    totalFetchedCount,
    loadingMore,
    handleLoadMore,
  } = useGetObjectives({
    onCompleted: handleRootDataLoaded(rootTeams),
    handleAppendRootData,
    rootTypesFilterKey: ROOT_TYPES_FILTER_KEY.GOALS_EXPLORER_ROOT,
    skip: !needsRootExplorerData || isEmpty(rootTeams),
  } as never);

  const { displayTreeData, onNodeSelected: onDisplayNodeSelected } =
    useDisplayTreeData({
      treeData: treeData as ObjectiveNode[],
      hasMore,
      loadingMore,
      handleLoadMore,
      totalFetchedCount,
      totalCount,
    });

  // Goals root route (/explore/goals) maps to the root team/group wrapper, not an objective id.
  const rootGroupNodeId = useMemo(
    () => (displayTreeData as ObjectiveNode[]).find((node) => node.group)?.id,
    [displayTreeData]
  );

  const resolvedActiveNodeId = useMemo(() => {
    if (onGoalsExploreRoot) {
      return rootGroupNodeId;
    }
    if (!activeGoalId) {
      return undefined;
    }
    return (
      findGoalNodeIdInTree(displayTreeData as ObjectiveNode[], activeGoalId) ??
      activeGoalId
    );
  }, [activeGoalId, displayTreeData, onGoalsExploreRoot, rootGroupNodeId]);

  const routeIsActive = onGoalsExploreRoot || !!activeGoalId;

  useEffect(() => {
    if (!initialDataFetched || onGoalsExploreRoot || !activeGoalId) {
      return;
    }

    const inTree = findGoalNodeIdInTree(
      displayTreeData as ObjectiveNode[],
      activeGoalId
    );
    if (inTree) {
      return;
    }
    if (branchLoadedForRef.current === activeGoalId) {
      return;
    }

    branchLoadedForRef.current = activeGoalId;
    loadBranch({
      nodeId: activeGoalId,
      maxChildDepth: MAX_CHILD_DEPTH,
      includeAssociatedTeams: true,
    });
  }, [
    activeGoalId,
    displayTreeData,
    initialDataFetched,
    loadBranch,
    onGoalsExploreRoot,
  ]);

  useEffect(() => {
    if (!routeIsActive || isEmpty(displayTreeData)) {
      return;
    }

    const pathTargetId = onGoalsExploreRoot
      ? rootGroupNodeId
      : (findGoalNodeIdInTree(
          displayTreeData as ObjectiveNode[],
          activeGoalId
        ) ?? activeGoalId);

    if (!pathTargetId) {
      return;
    }

    const path = findGoalPathToNode(displayTreeData, pathTargetId);
    if (!path?.length) {
      return;
    }

    setExpandedNodeIds((prev) => {
      const next = { ...prev };
      path.forEach((nodeId) => {
        next[nodeId] = true;
      });
      return next;
    });
  }, [
    activeGoalId,
    displayTreeData,
    onGoalsExploreRoot,
    rootGroupNodeId,
    routeIsActive,
  ]);

  const isGoalNodeActive = useCallback(
    (node: ObjectiveNode) =>
      routeIsActive &&
      !isNonSelectableGoalsTreeNode(node) &&
      matchesGoalExploreTarget(node, activeGoalId, onGoalsExploreRoot),
    [activeGoalId, onGoalsExploreRoot, routeIsActive]
  );

  const getGoalLinkTo = useCallback((node: ObjectiveNode) => {
    if (isNonSelectableGoalsTreeNode(node)) {
      return null;
    }
    if (node["objective"]) {
      return url(URL.GOALS_EXPLORE, {
        id: node["objective"]["id"] as string,
      });
    }
    if (node.group) {
      return url(URL.GOALS_EXPLORE, {});
    }
    return null;
  }, []);

  const onNodeSelected = useCallback(
    (node: ObjectiveNode) => {
      if (isNonSelectableGoalsTreeNode(node)) {
        onDisplayNodeSelected(node);
        return;
      }

      const goalHref = getGoalLinkTo(node);
      if (
        goalHref &&
        !matchesGoalExploreTarget(node, activeGoalId, onGoalsExploreRoot)
      ) {
        history.push(goalHref);
      }

      onDisplayNodeSelected(node);
    },
    [
      activeGoalId,
      getGoalLinkTo,
      history,
      onDisplayNodeSelected,
      onGoalsExploreRoot,
    ]
  );

  const onChevronToggle = useCallback(
    (node: ObjectiveNode, isOpen: boolean) => {
      if (isOpen) {
        userCollapsedIdsRef.current.delete(node.id);
      } else {
        userCollapsedIdsRef.current.add(node.id);
      }
      setExpandedNodeIds((prev) => ({ ...prev, [node.id]: isOpen }));

      if (!isOpen || node.group || isNonSelectableGoalsTreeNode(node)) {
        return;
      }
      loadBranch({ nodeId: node.id });
    },
    [loadBranch]
  );

  const getNodeOpenState = useCallback(
    (node: ObjectiveNode) => {
      if (userCollapsedIdsRef.current.has(node.id)) {
        return false;
      }
      if (expandedNodeIds[node.id]) {
        return true;
      }
      if (typeof node.toggled === "boolean" && node.toggled) {
        return true;
      }
      return false;
    },
    [expandedNodeIds]
  );

  const openStateStructureKey = useMemo(() => {
    const collectKeys = (node: ObjectiveNode): string[] => {
      const open = getNodeOpenState(node);
      const childKeys = (node.children ?? []).flatMap(collectKeys);
      return [`${node.id}:${open}`, ...childKeys];
    };
    return (displayTreeData as ObjectiveNode[]).flatMap(collectKeys).join("|");
  }, [displayTreeData, getNodeOpenState]);

  const customizeTreeHeader = useCallback<CustomizeTreeHeaderFn<ObjectiveNode>>(
    ({ node }, { onActivate }) => (
      <ObjectiveTreeHeader
        node={node}
        onActivate={onActivate}
        getGoalLinkTo={getGoalLinkTo}
      />
    ),
    [getGoalLinkTo]
  );

  const isLoading = loading || rootLoading;
  const combinedError = error || rootError;

  if (isLoading && isEmpty(treeData)) {
    return (
      <Box p="r">
        <P>Loading objectives...</P>
      </Box>
    );
  }

  if (combinedError) {
    return (
      <Box p="r">
        <P color="danger">
          Error loading objectives:{" "}
          {(combinedError as { message?: string })?.message ??
            String(combinedError)}
        </P>
      </Box>
    );
  }

  if (isEmpty(treeData)) {
    return (
      <Box p="r">
        <P>No objectives found</P>
      </Box>
    );
  }

  return (
    <Box>
      <H5 fontWeight="bold">Goals</H5>
      <Divider my="s" light />
      <TreeViewNext<ObjectiveNode>
        data={displayTreeData}
        onNodeSelected={onNodeSelected}
        onChevronToggle={onChevronToggle}
        suppressOnNodeSelectedWhenCollapsingBranch
        toggleBranchOnRowClick={false}
        customizeTreeHeader={customizeTreeHeader}
        activeNodeId={routeIsActive ? resolvedActiveNodeId : undefined}
        isNodeActive={isGoalNodeActive}
        getNodeOpenState={getNodeOpenState}
        openStateStructureKey={openStateStructureKey}
        syncOpenStateOnDataChange={false}
        openByDefault={false}
        scrollActiveNodeOnDataChange={routeIsActive}
      />
    </Box>
  );
};

export default GoalsExplore;
